import React, { useState, useMemo, useRef, useEffect } from "react";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import {
  ArrowLeft,
  MapPin,
  Star,
  ChevronDown,
  CheckCircle,
  AlertCircle,
  XCircle,
  Info,
  Users,
  Calendar,
  Search,
} from "lucide-react";
import CompactRoomSelector from "../components/CompactRoomSelector";
import Swal from "sweetalert2";
import Footer from "../components/Footer";

export default function DetailsPage({
  hotel: propHotel, 
  onBooking,
  searchData: propSearchData,
  margeB2B,
  onReSearch,
  currentMargin,
}) {
  // ✅ NEW: Local state from props
  const [hotel, setHotel] = useState(propHotel);
  const [searchData, setSearchData] = useState(propSearchData);
  const [isUpdatingSearch, setIsUpdatingSearch] = useState(false); // ✅ NEW: Loading state

  const [selectedPension, setSelectedPension] = useState(
    propHotel?.pensions && propHotel.pensions.length > 0
      ? propHotel.pensions[0].type
      : null,
  );
  const [expandedRooms, setExpandedRooms] = useState({});
  const [selectedAllocation, setSelectedAllocation] = useState(null);
  const [selectedRoomVariants, setSelectedRoomVariants] = useState({});

  const [editDateRange, setEditDateRange] = useState([
    {
      startDate: propSearchData?.checkIn
        ? parseDate(propSearchData.checkIn)
        : new Date(),
      endDate: propSearchData?.checkOut
        ? parseDate(propSearchData.checkOut)
        : new Date(new Date().setDate(new Date().getDate() + 3)),
      key: "selection",
    },
  ]);
  const [editRooms, setEditRooms] = useState(
    propSearchData?.rooms || [{ adults: 2, children: [] }],
  );
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarRef = useRef(null);
  const pensionSectionRef = useRef(null);
  // const isFirstRender = useRef(true);
  const hasClickedUpdate = useRef(false);

  const [displayHotel, setDisplayHotel] = useState(propHotel);

  function parseDate(dateStr) {
    if (dateStr instanceof Date) return dateStr;
    const parts = dateStr.split("/");
    if (parts.length === 3) {
      return new Date(parts[2], parts[1] - 1, parts[0]);
    }
    return new Date();
  }

  // ✅ NEW: Update local state when props change
  useEffect(() => {
    if (propHotel) {
      setHotel(propHotel);
      setDisplayHotel(propHotel);
      setIsUpdatingSearch(false); // ✅ Stop loading when hotel updates

      if (propHotel.pensions && propHotel.pensions.length > 0) {
        setSelectedPension(propHotel.pensions[0].type);
      }
      setSelectedRoomVariants({});
    }
    if (propSearchData) {
      setSearchData(propSearchData);
    }
  }, [propHotel, propSearchData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };
    if (showCalendar) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCalendar]);

useEffect(() => {
  if (propHotel) {
    setHotel(propHotel);
    setDisplayHotel(propHotel);
    setIsUpdatingSearch(false);

    if (propHotel.pensions && propHotel.pensions.length > 0) {
      setSelectedPension(propHotel.pensions[0].type);
    }
    setSelectedRoomVariants({});
  }
  if (propSearchData) {
    setSearchData(propSearchData);
  }
}, [propHotel, propSearchData]);

// ✅ FIXED: Scroll ONLY after user-triggered updates
useEffect(() => {
  const userTriggeredUpdate = localStorage.getItem('userTriggeredUpdate');
  
  if (
    userTriggeredUpdate === 'true' &&
    !isUpdatingSearch &&
    propHotel &&
    pensionSectionRef.current
  ) {
    setTimeout(() => {
      const yOffset = -100;
      const element = pensionSectionRef.current;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

      window.scrollTo({ top: y, behavior: "smooth" });
      localStorage.removeItem('userTriggeredUpdate');
    }, 300);
  }
}, [isUpdatingSearch, propHotel]);

  useEffect(() => {
    if (hotel && currentMargin !== undefined) {
      recalculateHotelPrices(currentMargin);
    }
  }, [currentMargin]);

  // ✅ IMPROVED: Recalculate ALL prices (pensions, roomsBySlot, fallbacks, selectedRooms)
  const recalculateHotelPrices = (newMargin) => {
    if (!hotel) return;


    // ✅ Step 1: Recalculate hotel pensions
    const updatedPensions = hotel.pensions.map((pension) => {
      // Recalculate rooms in roomsBySlot
      const updatedRoomsBySlot = {};
      if (pension.roomsBySlot) {
        Object.entries(pension.roomsBySlot).forEach(([slotKey, rooms]) => {
          updatedRoomsBySlot[slotKey] = rooms.map((room) => {
            const originalPrice = room.originalPrice;
            const totalMarginPercent = (margeB2B + newMargin) / 100;
            const priceWithBothMargins =
              originalPrice * (1 + totalMarginPercent);
            const gomaxCommission = originalPrice * (margeB2B / 100);
            const partnerProfit = originalPrice * (newMargin / 100);
            const purchasePrice = originalPrice + gomaxCommission;

            return {
              ...room,
              price: priceWithBothMargins,
              purchasePrice: purchasePrice,
              gomaxCommission: gomaxCommission,
              partnerProfit: partnerProfit,
            };
          });
        });
      }

      // Recalculate fallback suggestions
      const updatedFallbacks = {};
      if (pension.fallbackSuggestions) {
        Object.entries(pension.fallbackSuggestions).forEach(
          ([slotKey, fallback]) => {
            const updatedRooms = fallback.rooms.map((roomGroup) => ({
              ...roomGroup,
              availableRooms: roomGroup.availableRooms.map((room) => {
                const originalPrice = room.originalPrice;
                const totalMarginPercent = (margeB2B + newMargin) / 100;
                const priceWithBothMargins =
                  originalPrice * (1 + totalMarginPercent);
                const gomaxCommission = originalPrice * (margeB2B / 100);
                const partnerProfit = originalPrice * (newMargin / 100);
                const purchasePrice = originalPrice + gomaxCommission;

                return {
                  ...room,
                  price: priceWithBothMargins,
                  purchasePrice: purchasePrice,
                  gomaxCommission: gomaxCommission,
                  partnerProfit: partnerProfit,
                };
              }),
            }));

            updatedFallbacks[slotKey] = {
              ...fallback,
              rooms: updatedRooms,
            };
          },
        );
      }

      // Flatten all rooms for legacy "rooms" array
      const allRooms = Object.values(updatedRoomsBySlot).flat();

const updatedMinPrice = Object.keys(updatedRoomsBySlot).length > 0
  ? Object.values(updatedRoomsBySlot).reduce((sum, slotRooms) => {
      const cheapest = Math.min(...slotRooms.map(r => r.price));
      return sum + cheapest;
    }, 0)
  : Object.keys(updatedFallbacks).length > 0
    ? Object.values(updatedFallbacks).reduce((sum, fallback) => {
        if (!fallback?.rooms) return sum;
        return sum + fallback.rooms.reduce((groupSum, roomGroup) => {
          const cheapest = Math.min(...roomGroup.availableRooms.map(r => r.price));
          return groupSum + cheapest;
        }, 0);
      }, 0)
    : 0;

return {
  ...pension,
  rooms: allRooms,
  roomsBySlot: updatedRoomsBySlot,
  fallbackSuggestions: updatedFallbacks,
  minPrice: updatedMinPrice,
};
      // const allRooms = Object.values(updatedRoomsBySlot).flat();

      // return {
      //   ...pension,
      //   rooms: allRooms,
      //   roomsBySlot: updatedRoomsBySlot,
      //   fallbackSuggestions: updatedFallbacks,
      //   minPrice:
      //     allRooms.length > 0 ? Math.min(...allRooms.map((r) => r.price)) : 0,
      // };


    });

    const updatedHotel = {
      ...hotel,
      pensions: updatedPensions,
      price: updatedPensions.length > 0 ? updatedPensions[0].minPrice : 0,
    };

    setDisplayHotel(updatedHotel);

    // ✅ Step 2: Recalculate selectedRoomVariants
    if (Object.keys(selectedRoomVariants).length > 0) {
      const updatedSelectedRooms = {};

      Object.entries(selectedRoomVariants).forEach(
        ([slotKey, selectedRoom]) => {
          if (!selectedRoom) return;

          // ✅ Recalculate this specific room's prices
          const originalPrice = selectedRoom.originalPrice;
          const totalMarginPercent = (margeB2B + newMargin) / 100;
          const priceWithBothMargins = originalPrice * (1 + totalMarginPercent);
          const gomaxCommission = originalPrice * (margeB2B / 100);
          const partnerProfit = originalPrice * (newMargin / 100);
          const purchasePrice = originalPrice + gomaxCommission;

          updatedSelectedRooms[slotKey] = {
            ...selectedRoom,
            price: priceWithBothMargins,
            purchasePrice: purchasePrice,
            gomaxCommission: gomaxCommission,
            partnerProfit: partnerProfit,
          };
        },
      );


      setSelectedRoomVariants(updatedSelectedRooms);
    }
  };

  const handleDetailsSearch = () => {
    if (onReSearch) {
      setIsUpdatingSearch(true); // ✅ START LOADING

      onReSearch({
        hotelId: hotel.id,
        destination: searchData.destinationId,
        checkIn: editDateRange[0].startDate,
        checkOut: editDateRange[0].endDate,
        rooms: editRooms,
      });
    }
  };

  // Calculate nights from searchData
  const calculateNights = useMemo(() => {
    if (!searchData?.checkIn || !searchData?.checkOut) return 1;

    const parseDate = (dateStr) => {
      if (dateStr instanceof Date) return dateStr;

      const parts = dateStr.split("/");
      if (parts.length === 3) {
        const day = parseInt(parts[0]);
        const month = parseInt(parts[1]) - 1;
        const year = parseInt(parts[2]);
        return new Date(year, month, day);
      }

      return new Date(dateStr);
    };

    const checkIn = parseDate(searchData.checkIn);
    const checkOut = parseDate(searchData.checkOut);

    const diffTime = Math.abs(checkOut - checkIn);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays || 1;
  }, [searchData]);

  const calculateTotalPrice = (totalPrice) => {
    return parseFloat(totalPrice);
  };

  const currentPension = displayHotel?.pensions?.find(
    (p) => p.type === selectedPension,
  );

  // Calculate total guests
  const totalGuests = useMemo(() => {
    if (!searchData?.rooms) return { adults: 2, children: 0 };

    const adults = searchData.rooms.reduce(
      (sum, room) => sum + (room.adults || 0),
      0,
    );
    const children = searchData.rooms.reduce(
      (sum, room) => sum + (room.children?.length || 0),
      0,
    );

    return { adults, children, total: adults + children };
  }, [searchData]);

  const roomSelectionData = useMemo(() => {
    if (!currentPension) return null;
    if (!searchData?.rooms) return null;

    const roomSlots = searchData.rooms
      .map((requestedRoom, index) => {
        // ✅ Try exact match first
        const exactMatchRooms = currentPension.roomsBySlot?.[index] || [];

        if (exactMatchRooms.length > 0) {
          const sortedRooms = [...exactMatchRooms].sort(
            (a, b) => parseFloat(a.price) - parseFloat(b.price),
          );

          return {
            slotIndex: index,
            requestedAdults: requestedRoom.adults,
            requestedChildren: requestedRoom.children?.length || 0,
            totalPeople:
              requestedRoom.adults + (requestedRoom.children?.length || 0),
            availableRooms: sortedRooms,
            defaultSelection: sortedRooms[0],
            matchType: "exact",
          };
        }

        // ✅ No exact match? Try fallback
        const fallback = currentPension.fallbackSuggestions?.[index];
        if (fallback) {
          return {
            slotIndex: index,
            requestedAdults: requestedRoom.adults,
            requestedChildren: requestedRoom.children?.length || 0,
            totalPeople:
              requestedRoom.adults + (requestedRoom.children?.length || 0),
            fallback: fallback,
            matchType: "fallback",
          };
        }

        return null;
      })
      .filter((slot) => slot !== null);

    return roomSlots.length > 0 ? roomSlots : null;
  }, [currentPension, searchData]);

  // ✅ Auto-select default rooms
  useEffect(() => {
    if (roomSelectionData) {
      const defaultSelections = {};
      roomSelectionData.forEach((slot) => {
        if (slot.matchType === "exact" && slot.defaultSelection) {
          defaultSelections[slot.slotIndex] = slot.defaultSelection;
        }
      });
      setSelectedRoomVariants(defaultSelections);
    }
  }, [roomSelectionData]);

  // ✅ Update specific room variant
  const handleVariantChange = (roomIndex, variant) => {
    setSelectedRoomVariants((prev) => ({
      ...prev,
      [roomIndex]: variant,
    }));
  };

  // ✅ Calculate total price
  const totalPrice = useMemo(() => {
    if (!roomSelectionData) return 0;

    const total = Object.values(selectedRoomVariants).reduce(
      (sum, room) => sum + parseFloat(room?.price || 0),
      0,
    );


    return total;
  }, [roomSelectionData, selectedRoomVariants]);

  const toggleRoomExpansion = (roomId) => {
    setExpandedRooms((prev) => ({ ...prev, [roomId]: !prev[roomId] }));
  };

  const parseCancellationDate = (dateString) => {
    if (!dateString) return null;

    try {
      const cleaned = String(dateString).trim();

      const match = cleaned.match(
        /^(\d{1,2})-(\d{1,2})-(\d{4})\s+(\d{1,2}):(\d{1,2})$/,
      );

      if (match) {
        const [, day, month, year, hour, minute] = match;
        const date = new Date(
          parseInt(year),
          parseInt(month) - 1,
          parseInt(day),
          parseInt(hour),
          parseInt(minute),
        );

        if (!isNaN(date.getTime())) {
          return date;
        }
      }

      return null;
    } catch (e) {
      return null;
    }
  };

  const formatCancellationPolicy = (policy) => {
    if (!policy || policy.length === 0) {
      return [{ text: "Non remboursable", type: "danger" }];
    }

    return policy.map((p) => {
      let text = "";
      let type = "info";

      if (p.Nature === "BEFORE_ARRIVAL") {
        const feeAmount = p.Fees || 0;

        if (p.Type === "PERCENT") {
          text = `${feeAmount}% de pénalité`;
        } else if (p.Type === "NIGHT") {
          text = `${feeAmount} nuit(s) de pénalité`;
        } else if (p.Type === "PRICE") {
          text = `${feeAmount} TND de pénalité`;
        } else {
          text = `${feeAmount} de pénalité`;
        }

        if (p.FromDate) {
          const date = parseCancellationDate(p.FromDate);
          if (date && !isNaN(date.getTime())) {
            try {
              text += ` à partir du ${date.toLocaleDateString("fr-FR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })} à ${date.toLocaleTimeString("fr-FR", {
                hour: "2-digit",
                minute: "2-digit",
              })}`;
            } catch (e) {
              text += ` (date: ${p.FromDate})`;
            }
          }
        }
        type = "warning";
      } else if (p.Nature === "NO_SHOW") {
        text = `Non-présentation: ${p.Fees || 100}% de pénalité`;
        type = "danger";
      } else if (p.Nature === "PREMATURE_DEPARTURE") {
        text = `Départ anticipé: ${p.Fees || 100}% de pénalité`;
        type = "danger";
      } else {
        text = `Politique d'annulation: ${p.Fees || 0}${p.Type === "PERCENT" ? "%" : " TND"}`;
        type = "info";
      }

      return { text, type };
    });
  };

  const formatPrice = (price) => {
    const numPrice = parseFloat(price);
    return isNaN(numPrice) ? "0.000" : numPrice.toFixed(3);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-grow">
        <div className="container mx-auto px-4 py-6">
          <button
            onClick={() => window.history.back()}
            className="mb-4 flex items-center gap-2 text-[#2B4979] hover:text-[#FF7B47] font-semibold transition group"
          >
            <ArrowLeft
              size={20}
              className="group-hover:-translate-x-1 transition-transform"
            />
            Retour aux résultats
          </button>

          {/* Editable Search Bar */}
          <div className="bg-gradient-to-r from-blue-50 to-orange-50 border-2 border-[#FF7B47] rounded-xl p-4 mb-6 shadow-md">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-[#2B4979] flex items-center gap-2">
                <Search size={20} className="text-[#FF7B47]" />
                Modifier dates et voyageurs
              </h3>
              <div className="text-sm text-gray-600">
                <span className="font-semibold">{searchData?.destination}</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Dates - Editable */}
              <div className="relative" ref={calendarRef}>
                <label className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
                  <Calendar size={12} className="text-[#FF7B47]" />
                  Dates de séjour
                </label>
                <button
                  type="button"
                  onClick={() => setShowCalendar(!showCalendar)}
                  className="w-full px-3 py-2 border-2 border-blue-300 rounded-lg text-left hover:border-[#FF7B47] hover:bg-blue-50 transition bg-white text-sm"
                >
                  {editDateRange[0].startDate.toLocaleDateString("fr-FR")} -{" "}
                  {editDateRange[0].endDate.toLocaleDateString("fr-FR")}
                </button>

                {showCalendar && (
                  <div className="absolute top-full left-0 mt-2 z-50 bg-white rounded-lg shadow-2xl border-2 border-gray-200">
                    <DateRange
                      ranges={editDateRange}
                      onChange={(item) => setEditDateRange([item.selection])}
                      months={2}
                      direction="horizontal"
                      minDate={new Date()}
                      rangeColors={["#FF7B47"]}
                    />
                    <div className="p-4 border-t">
                      <button
                        type="button"
                        onClick={() => setShowCalendar(false)}
                        className="w-full bg-[#2B4979] text-white py-2 rounded-lg hover:bg-[#1e3454] transition text-sm"
                      >
                        Confirmer
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Rooms - Editable */}
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
                  <Users size={12} className="text-[#FF7B47]" />
                  Voyageurs
                </label>
                <div className="h-[38px]">
                  <CompactRoomSelector
                    rooms={editRooms}
                    setRooms={setEditRooms}
                  />
                </div>
              </div>

              {/* Search Button */}
              <div className="flex items-end">
                <button
                  onClick={handleDetailsSearch}
                  disabled={isUpdatingSearch}
                  className="w-full bg-[#FF7B47] hover:bg-[#ff6a30] disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition font-semibold text-sm flex items-center justify-center gap-2"
                >
                  {isUpdatingSearch ? (
                    <>
                      <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Mise à jour...
                    </>
                  ) : (
                    <>
                      <Search size={16} />
                      Mettre à jour
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="mt-3 bg-white/80 rounded-lg p-2 text-xs text-gray-600 flex items-start gap-2">
              <Info size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
              <p>
                Modification des dates et du nombre de voyageurs pour cet hôtel
                uniquement. Pour changer de destination, retournez aux résultats
                de recherche.
              </p>
            </div>
          </div>

          {searchData && (
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg mb-6">
              <div className="flex items-center gap-2 text-[#2B4979]">
                <Users size={20} />
                <span className="font-semibold">
                  Votre recherche: {totalGuests.adults} adulte(s)
                  {totalGuests.children > 0 &&
                    `, ${totalGuests.children} enfant(s)`}
                </span>
              </div>
            </div>
          )}

          {/* Hotel Header */}
          <div className="bg-white rounded-xl shadow-md p-6 mb-6">
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="lg:w-96 h-64 rounded-lg overflow-hidden">
                <img
                  src={displayHotel?.image}
                  alt={displayHotel?.name}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="flex-1">
                <h1 className="text-3xl font-bold text-[#2B4979] mb-3">
                  {displayHotel?.name}
                </h1>

                <div className="flex items-center gap-2 mb-3">
                  <div className="flex items-center gap-1">
                    {[...Array(displayHotel?.stars || 3)].map((_, i) => (
                      <Star
                        key={i}
                        size={18}
                        className="fill-yellow-400 text-yellow-400"
                      />
                    ))}
                  </div>
                  {displayHotel?.starCategory && (
                    <span className="text-sm text-gray-500">
                      ({displayHotel.starCategory})
                    </span>
                  )}
                </div>

                <div className="flex items-start gap-2 text-gray-600 mb-4">
                  <MapPin size={16} className="text-[#FF7B47] mt-1" />
                  <span className="text-sm">
                    {displayHotel?.rawData?.Hotel?.Adress ||
                      displayHotel?.location}
                  </span>
                </div>

                {displayHotel?.rawData?.Hotel?.Theme &&
                  displayHotel.rawData.Hotel.Theme.length > 0 && (
                    <div className="mb-4">
                      <span className="text-sm font-semibold text-gray-700 mb-2 block">
                        Thèmes:
                      </span>
                      <div className="flex flex-wrap gap-2">
                        {displayHotel.rawData.Hotel.Theme.map((theme, idx) => (
                          <span
                            key={idx}
                            className="bg-blue-50 text-blue-700 text-xs px-3 py-1 rounded-full border border-blue-200"
                          >
                            {theme}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                {displayHotel?.rawData?.Hotel?.Note && (
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded text-sm text-gray-700">
                    <div className="flex gap-2">
                      <Info
                        size={16}
                        className="text-yellow-600 flex-shrink-0 mt-0.5"
                      />
                      <div
                        dangerouslySetInnerHTML={{
                          __html: displayHotel.rawData.Hotel.Note,
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Pension Selector */}
          {displayHotel?.pensions && displayHotel.pensions.length > 0 && (
            <div
              className="bg-white rounded-xl shadow-md p-6 mb-6"
              ref={pensionSectionRef}
            >
              <h2 className="text-xl font-bold text-[#2B4979] mb-4">
                Sélectionnez une formule d'hébergement
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {displayHotel.pensions.map((pension) => (
                  <button
                    key={pension.type}
                    onClick={() => {
                      setSelectedPension(pension.type);
                      setSelectedRoomVariants({});
                    }}
                    className={`p-4 rounded-lg border-2 transition text-left ${
                      selectedPension === pension.type
                        ? "border-[#FF7B47] bg-orange-50 shadow-md"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="font-semibold text-[#2B4979] mb-1 text-sm">
                      {pension.label}
                    </div>
                    <div className="text-xs text-gray-500">{pension.code}</div>
                    <div className="text-[#FF7B47] font-bold mt-2">
                      À partir de {formatPrice(pension.minPrice)} TND
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ✅ FIXED: Room Selection with Loading Overlay */}
          {roomSelectionData && (
            <div className="bg-white rounded-xl shadow-md p-6 mb-6 relative">
              {/* ✅ Loading Overlay */}
              {isUpdatingSearch && (
                <div className="absolute inset-0 bg-white/90 backdrop-blur-sm rounded-xl z-10 flex items-center justify-center">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF7B47] mb-3"></div>
                    <p className="text-[#2B4979] font-semibold">
                      Mise à jour des disponibilités...
                    </p>
                  </div>
                </div>
              )}

              <h2 className="text-xl font-bold text-[#2B4979] mb-4">
                Sélectionnez vos chambres
              </h2>

              <div className="space-y-6">
                {roomSelectionData.map((slot, slotIndex) => (
                  <div key={slotIndex}>
                    {/* ✅ EXACT MATCH - Show normal room selection */}
                    {slot.matchType === "exact" && (
                      <div className="border-2 border-gray-200 rounded-lg p-4">
                        <h3 className="font-semibold text-[#2B4979] mb-3 flex items-center gap-2">
                          <Users size={18} />
                          Chambre {slotIndex + 1}: {slot.requestedAdults}{" "}
                          adulte(s)
                          {slot.requestedChildren > 0 &&
                            ` + ${slot.requestedChildren} enfant(s)`}
                          <span className="text-sm text-gray-500 ml-2">
                            ({slot.totalPeople} personne
                            {slot.totalPeople > 1 ? "s" : ""})
                          </span>
                        </h3>

                        <div className="space-y-2">
                          {slot.availableRooms.map((room, roomIdx) => {
                            const isSelected =
                              selectedRoomVariants[slotIndex]?.id === room.id;
                            const expandKey = `${slotIndex}-${room.id}-${roomIdx}`;

                            return (
                              <div
                                key={expandKey}
                                className={`border-2 rounded-lg p-3 cursor-pointer transition ${
                                  isSelected
                                    ? "border-[#FF7B47] bg-orange-50"
                                    : "border-gray-200 hover:border-gray-300"
                                }`}
                                onClick={() =>
                                  handleVariantChange(slotIndex, room)
                                }
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3 flex-1">
                                    <input
                                      type="radio"
                                      checked={isSelected}
                                      onChange={() =>
                                        handleVariantChange(slotIndex, room)
                                      }
                                      className="w-4 h-4 text-[#FF7B47]"
                                    />
                                    <div className="flex-1">
                                      <div className="font-bold text-[#2B4979]">
                                        {room.name}
                                      </div>
                                      <div className="flex items-center gap-2 flex-wrap mt-1">
                                        {room.onRequest && (
                                          <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                                            Sur demande
                                          </span>
                                        )}
                                        {room.notRefundable && (
                                          <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                                            Non remboursable
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-right ml-4">
                                    <div className="text-xl font-bold text-[#FF7B47]">
                                      {formatPrice(room.price)} TND
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      pour {calculateNights} nuit(s)
                                    </div>
                                    <div className="text-xs text-gray-400">
                                      (
                                      {formatPrice(
                                        room.price / calculateNights,
                                      )}{" "}
                                      TND/nuit)
                                    </div>
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleRoomExpansion(expandKey);
                                    }}
                                    className="p-2 hover:bg-gray-200 rounded-full transition ml-2"
                                  >
                                    <ChevronDown
                                      size={20}
                                      className={`text-[#2B4979] transition-transform ${
                                        expandedRooms[expandKey]
                                          ? "rotate-180"
                                          : ""
                                      }`}
                                    />
                                  </button>
                                </div>

                                {/* Expanded details */}
                                {expandedRooms[expandKey] && (
                                  <div className="mt-3 pt-3 border-t-2 border-gray-200">
                                    {room.cancellationPolicy &&
                                      room.cancellationPolicy.length > 0 && (
                                        <div>
                                          <h4 className="font-semibold text-[#2B4979] mb-2">
                                            Conditions d'annulation:
                                          </h4>
                                          <div className="space-y-2">
                                            {formatCancellationPolicy(
                                              room.cancellationPolicy,
                                            ).map((policy, idx) => (
                                              <div
                                                key={idx}
                                                className={`text-sm p-2 rounded flex items-start gap-2 ${
                                                  policy.type === "danger"
                                                    ? "bg-red-50 text-red-700"
                                                    : policy.type === "warning"
                                                      ? "bg-yellow-50 text-yellow-700"
                                                      : "bg-blue-50 text-blue-700"
                                                }`}
                                              >
                                                <Info
                                                  size={14}
                                                  className="mt-0.5 flex-shrink-0"
                                                />
                                                <span>{policy.text}</span>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* ✅ FALLBACK - Show split suggestion */}
                    {slot.matchType === "fallback" && slot.fallback && (
                      <div className="border-2 border-orange-300 rounded-lg p-4 bg-orange-50">
                        <div className="flex items-start gap-3 mb-4">
                          <AlertCircle
                            size={20}
                            className="text-orange-600 flex-shrink-0 mt-1"
                          />
                          <div>
                            <h3 className="font-semibold text-[#2B4979] mb-1">
                              Chambre demandée: {slot.requestedAdults} adulte(s)
                              {slot.requestedChildren > 0 &&
                                ` + ${slot.requestedChildren} enfant(s)`}
                            </h3>
                            <p className="text-sm text-orange-700">
                              ⚠️ Cet hôtel ne dispose pas d'une chambre unique
                              pour {slot.totalPeople} personnes.
                              <br />
                              <strong>Solution alternative:</strong>{" "}
                              {slot.fallback.description}
                            </p>
                          </div>
                        </div>

                        {/* Show split rooms */}
                        {slot.fallback.rooms.map((splitRoom, splitIdx) => (
                          <div
                            key={splitIdx}
                            className="mt-4 border-t-2 border-orange-200 pt-4 first:border-t-0 first:mt-0 first:pt-0"
                          >
                            <h4 className="font-semibold text-[#2B4979] mb-2 flex items-center gap-2">
                              <Users size={16} />
                              Sous-chambre {slotIndex + 1}.{splitIdx + 1}:{" "}
                              {splitRoom.config.adults} adulte(s)
                              {splitRoom.config.children > 0 &&
                                ` + ${splitRoom.config.children} enfant(s)`}
                            </h4>

                            <div className="space-y-2">
                              {splitRoom.availableRooms.map((room, roomIdx) => {
                                const fallbackKey = `${slotIndex}-${splitIdx}`;
                                const isSelected =
                                  selectedRoomVariants[fallbackKey]?.id ===
                                  room.id;
                                const expandKey = `${fallbackKey}-${room.id}-${roomIdx}`;

                                return (
                                  <div
                                    key={expandKey}
                                    className={`border-2 rounded-lg p-3 cursor-pointer transition bg-white ${
                                      isSelected
                                        ? "border-[#FF7B47] shadow-md"
                                        : "border-orange-200 hover:border-orange-400"
                                    }`}
                                    onClick={() =>
                                      handleVariantChange(fallbackKey, room)
                                    }
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-3 flex-1">
                                        <input
                                          type="radio"
                                          checked={isSelected}
                                          onChange={() =>
                                            handleVariantChange(
                                              fallbackKey,
                                              room,
                                            )
                                          }
                                          className="w-4 h-4 text-[#FF7B47]"
                                        />
                                        <div className="flex-1">
                                          <div className="font-bold text-[#2B4979]">
                                            {room.name}
                                          </div>
                                          <div className="flex items-center gap-2 flex-wrap mt-1">
                                            {room.onRequest && (
                                              <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                                                Sur demande
                                              </span>
                                            )}
                                            {room.notRefundable && (
                                              <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                                                Non remboursable
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="text-right ml-4">
                                        <div className="text-xl font-bold text-[#FF7B47]">
                                          {formatPrice(room.price)} TND
                                        </div>
                                        <div className="text-xs text-gray-500">
                                          pour {calculateNights} nuit(s)
                                        </div>
                                        <div className="text-xs text-gray-400">
                                          (
                                          {formatPrice(
                                            room.price / calculateNights,
                                          )}{" "}
                                          TND/nuit)
                                        </div>
                                      </div>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleRoomExpansion(expandKey);
                                        }}
                                        className="p-2 hover:bg-gray-200 rounded-full transition ml-2"
                                      >
                                        <ChevronDown
                                          size={20}
                                          className={`text-[#2B4979] transition-transform ${
                                            expandedRooms[expandKey]
                                              ? "rotate-180"
                                              : ""
                                          }`}
                                        />
                                      </button>
                                    </div>

                                    {/* Expanded details for fallback rooms */}
                                    {expandedRooms[expandKey] && (
                                      <div className="mt-3 pt-3 border-t-2 border-gray-200">
                                        {room.cancellationPolicy &&
                                          room.cancellationPolicy.length >
                                            0 && (
                                            <div>
                                              <h4 className="font-semibold text-[#2B4979] mb-2">
                                                Conditions d'annulation:
                                              </h4>
                                              <div className="space-y-2">
                                                {formatCancellationPolicy(
                                                  room.cancellationPolicy,
                                                ).map((policy, idx) => (
                                                  <div
                                                    key={idx}
                                                    className={`text-sm p-2 rounded flex items-start gap-2 ${
                                                      policy.type === "danger"
                                                        ? "bg-red-50 text-red-700"
                                                        : policy.type ===
                                                            "warning"
                                                          ? "bg-yellow-50 text-yellow-700"
                                                          : "bg-blue-50 text-blue-700"
                                                    }`}
                                                  >
                                                    <Info
                                                      size={14}
                                                      className="mt-0.5 flex-shrink-0"
                                                    />
                                                    <span>{policy.text}</span>
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Total Price */}
              {Object.keys(selectedRoomVariants).length ===
                roomSelectionData.length && (
                <div className="mt-6 p-4 bg-green-50 border-l-4 border-green-500 rounded">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-green-800">
                      Prix total pour toutes les chambres:
                    </div>
                    <div className="text-2xl font-bold text-green-800">
                      {formatPrice(totalPrice)} TND
                      <span className="text-sm font-normal text-green-600 ml-2">
                        pour {calculateNights} nuit(s)
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Booking Button */}
          {roomSelectionData &&
            Object.keys(selectedRoomVariants).length ===
              roomSelectionData.length && (
              <div className="bg-gradient-to-r from-[#2B4979] to-[#3d5c8f] rounded-xl shadow-xl p-8 text-white text-center">
                <h2 className="text-2xl md:text-3xl font-bold mb-3">
                  Prêt à réserver ?
                </h2>
                <p className="text-white/80 mb-6 max-w-2xl mx-auto">
                  Total: {formatPrice(totalPrice)} TND pour {calculateNights}{" "}
                  nuit(s)
                </p>
                <button
                  onClick={() =>
                    onBooking(
                      displayHotel,
                      currentPension,
                      selectedRoomVariants,
                      roomSelectionData,
                    )
                  }
                  className="bg-[#FF7B47] hover:bg-[#ff6a30] text-white font-bold px-12 py-4 rounded-lg transition duration-200 transform hover:scale-105 shadow-xl text-lg"
                >
                  Demander un devis
                </button>
              </div>
            )}

          {/* Facilities */}
          {displayHotel?.facilities && displayHotel.facilities.length > 0 && (
            <div className="bg-white rounded-xl shadow-md p-6 mt-6">
              <h2 className="text-xl font-bold text-[#2B4979] mb-4">
                Équipements & Services
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {displayHotel.facilities.map((facility, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 text-sm text-gray-700"
                  >
                    <CheckCircle size={16} className="text-green-500" />
                    <span>{facility}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
